/**
 * CR500: useDealCardSpecV2 Hook
 * CR422: Added environment-aware SSOT loading + source tracking
 * 
 * React hook for loading and using DealCardSpec v2.
 * Provides loading state, error handling, and caching.
 */

import { useState, useEffect, useCallback, useMemo } from "react";
import type { ResolvedDealCardSpecV2 } from "@/lib/deal-cards/specV2/types";
import {
  loadDealCardSpecV2,
  isDealCardSpecV2Enabled,
  getCachedSpec,
  getFallbackSpec,
  clearSpecCacheForType,
  getCachedSpecSource,
  type SpecSource,
} from "@/lib/deal-cards/specV2/loadSpec";
import { getDealType } from "@/lib/deals/getDealType";
import type { Deal } from "@/lib/types/deals";

// ================================================================
// TYPES
// ================================================================

export interface UseDealCardSpecV2Options {
  /** Deal type key (e.g., "BONUS", "DISCOUNT") */
  dealTypeKey?: string;
  /** Alternatively, pass a deal and type will be inferred */
  deal?: Deal;
  /** Skip loading (useful for conditional rendering) */
  skip?: boolean;
  /** Use cached value immediately while loading fresh data */
  useCachedWhileLoading?: boolean;
  /** CR422: Environment for SSOT loading (staging, preview, production) */
  environment?: string;
}

export interface UseDealCardSpecV2Result {
  /** The resolved spec (or null if loading/error) */
  spec: ResolvedDealCardSpecV2 | null;
  /** Loading state */
  loading: boolean;
  /** Error message if failed */
  error: string | null;
  /** Whether v2 spec is enabled globally */
  v2Enabled: boolean;
  /** Refetch the spec */
  refetch: () => Promise<void>;
  /** CR422: Source metadata for debug display */
  specSource: SpecSource | null;
}

// ================================================================
// HOOK
// ================================================================

export function useDealCardSpecV2(
  options: UseDealCardSpecV2Options = {}
): UseDealCardSpecV2Result {
  const { dealTypeKey: explicitKey, deal, skip = false, useCachedWhileLoading = true, environment } = options;
  
  // Infer deal type from deal if not provided
  const dealTypeKey = useMemo(() => {
    if (explicitKey) return explicitKey.toUpperCase();
    if (deal) {
      const { category } = getDealType(deal);
      return category;
    }
    return null;
  }, [explicitKey, deal]);
  
  const [spec, setSpec] = useState<ResolvedDealCardSpecV2 | null>(() => {
    // Initialize with cached value if available
    if (dealTypeKey && useCachedWhileLoading) {
      return getCachedSpec(dealTypeKey);
    }
    return null;
  });
  const [loading, setLoading] = useState(!skip && !!dealTypeKey);
  const [error, setError] = useState<string | null>(null);
  const [v2Enabled, setV2Enabled] = useState(false);
  const [specSource, setSpecSource] = useState<SpecSource | null>(null);
  
  // Fetch spec
  const fetchSpec = useCallback(async () => {
    if (!dealTypeKey || skip) {
      setLoading(false);
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      // Check if v2 is enabled
      const enabled = await isDealCardSpecV2Enabled();
      setV2Enabled(enabled);
      
      // CR422: Load the spec with optional environment for SSOT
      const loadedSpec = await loadDealCardSpecV2(dealTypeKey, environment);
      setSpec(loadedSpec);
      
      // CR422: Get source metadata
      const source = getCachedSpecSource(dealTypeKey, environment);
      setSpecSource(source);
    } catch (err) {
      console.error("[useDealCardSpecV2] Error loading spec:", err);
      setError(String(err));
      
      // Fall back to default spec
      setSpec(getFallbackSpec(dealTypeKey));
      setSpecSource({ type: "fallback", dealType: dealTypeKey });
    } finally {
      setLoading(false);
    }
  }, [dealTypeKey, skip, environment]);
  
  // Initial fetch
  useEffect(() => {
    fetchSpec();
  }, [fetchSpec]);
  
  // Refetch function
  const refetch = useCallback(async () => {
    if (dealTypeKey) {
      clearSpecCacheForType(dealTypeKey);
    }
    await fetchSpec();
  }, [dealTypeKey, fetchSpec]);
  
  return {
    spec,
    loading,
    error,
    v2Enabled,
    refetch,
    specSource,
  };
}

// ================================================================
// CONVENIENCE HOOKS
// ================================================================

/**
 * Hook to check if v2 spec is enabled.
 * Caches the result for the session.
 */
let cachedV2Enabled: boolean | null = null;

export function useIsSpecV2Enabled(): { enabled: boolean; loading: boolean } {
  const [enabled, setEnabled] = useState(cachedV2Enabled ?? false);
  const [loading, setLoading] = useState(cachedV2Enabled === null);
  
  useEffect(() => {
    if (cachedV2Enabled !== null) {
      setEnabled(cachedV2Enabled);
      setLoading(false);
      return;
    }
    
    isDealCardSpecV2Enabled().then((result) => {
      cachedV2Enabled = result;
      setEnabled(result);
      setLoading(false);
    });
  }, []);
  
  return { enabled, loading };
}

/**
 * Clear the cached v2 enabled state (for testing/admin use).
 */
export function clearV2EnabledCache(): void {
  cachedV2Enabled = null;
}
